import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Plus,
  Hotel,
  Bus,
  Plane,
  Users,
  Calendar,
  Layers,
  X,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';
import { AllocationConsumptionModal } from './allocation-consumption-modal';
import { BookingItemModal } from './booking-item-modal';
import { SupplierPaymentModal } from './supplier-payment-modal';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { format } from 'date-fns';

type AllocationType = 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT';

interface AllocationConsumption {
  id: string;
  units: number;
  serviceDate: Date;
  endDate: Date | null;
  unitCost: number;
  totalCost: number;
  currency: string;
  consumedAt: Date;
  notes: string | null;
  allocation: {
    id: string;
    name: string;
    allocationType: AllocationType;
    partner: {
      id: string;
      companyName: string;
    };
  };
}

interface BookingItem {
  id: string;
  serviceType: string;
  name: string;
  description?: string | null;
  serviceDate: Date | null;
  endDate: Date | null;
  quantity: number;
  unitCost: number;
  totalCost: number;
  sellingPrice: number;
  currency: string;
  confirmationStatus: string;
  supplierPaymentStatus?: string | null;
  supplierPaidAmount?: number | null;
  customerPaidAmount?: number | null;
  partnerId?: string | null;
  partner: {
    id: string;
    companyName: string;
  } | null;
  allocationConsumptions: AllocationConsumption[];
}

interface BookingItemsCardProps {
  bookingId: string;
  items: BookingItem[];
  defaultServiceDate?: Date;
  defaultEndDate?: Date;
  onSuccess?: () => void;
}

const typeIcons: Record<AllocationType, typeof Hotel> = {
  HOTEL_ROOM: Hotel,
  TOUR_SEAT: Users,
  TRANSFER_VEHICLE: Bus,
  FLIGHT_SEAT: Plane,
};

const typeColors: Record<AllocationType, string> = {
  HOTEL_ROOM: 'bg-blue-100 text-blue-700',
  TOUR_SEAT: 'bg-purple-100 text-purple-700',
  TRANSFER_VEHICLE: 'bg-green-100 text-green-700',
  FLIGHT_SEAT: 'bg-sky-100 text-sky-700',
};

const serviceTypeLabels: Record<string, string> = {
  ACCOMMODATION: 'Accommodation',
  TRANSPORT: 'Transport',
  ACTIVITY: 'Activity',
  MEAL: 'Meal',
  GUIDE: 'Guide',
  INSURANCE: 'Insurance',
  TRANSFER: 'Transfer',
  OTHER: 'Other',
  // Flight types
  flight_domestic: 'Domestic Flight',
  flight_international: 'International Flight',
  FLIGHT_DOMESTIC: 'Domestic Flight',
  FLIGHT_INTERNATIONAL: 'International Flight',
  // Additional service types
  train: 'Train Ticket',
  bus: 'Bus Ticket',
  car_rental: 'Car Rental',
  cruise_cabin: 'Cruise Cabin',
  camping: 'Camping / Glamping',
};

export function BookingItemsCard({
  bookingId,
  items,
  defaultServiceDate,
  defaultEndDate,
  onSuccess,
}: BookingItemsCardProps) {
  const { t } = useTranslation('bookings');
  const { t: tAllocations } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');

  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BookingItem | null>(null);
  const [isConsumptionModalOpen, setIsConsumptionModalOpen] = useState(false);
  const [releaseConfirm, setReleaseConfirm] = useState<{
    consumptionId: string;
    allocationName: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    itemId: string;
    itemName: string;
  } | null>(null);
  const [supplierPaymentModalOpen, setSupplierPaymentModalOpen] = useState(false);
  const [selectedItemForSupplierPayment, setSelectedItemForSupplierPayment] = useState<{
    id: string;
    name: string;
    totalCost: number;
    supplierPaidAmount: number | null;
    currency: string;
    partner?: { companyName: string } | null;
  } | null>(null);

  const releaseMutation = trpc.allocation.release.useMutation({
    onSuccess: () => {
      showSuccessToast(tAllocations('messages.released') || 'Allocation released successfully');
      setReleaseConfirm(null);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deleteItemMutation = trpc.booking.deleteItem.useMutation({
    onSuccess: () => {
      showSuccessToast(t('items.deleted') || 'Item deleted successfully');
      setDeleteConfirm(null);
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleConsumeAllocation = (item: BookingItem) => {
    setSelectedItem(item);
    setIsConsumptionModalOpen(true);
  };

  const handleReleaseAllocation = (consumptionId: string, allocationName: string) => {
    setReleaseConfirm({ consumptionId, allocationName });
  };

  const handleDeleteItem = (itemId: string, itemName: string) => {
    setDeleteConfirm({ itemId, itemName });
  };

  const confirmRelease = () => {
    if (releaseConfirm) {
      releaseMutation.mutate({
        consumptionId: releaseConfirm.consumptionId,
        reason: 'Released by staff',
      });
    }
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      deleteItemMutation.mutate(deleteConfirm.itemId);
    }
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold flex items-center gap-2">
          <Package className="h-5 w-5" />
          {t('items.title') || 'Booking Items'} ({items.length})
        </h2>
        <Button variant="outline" size="sm" onClick={() => setIsAddItemModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('items.addItem') || 'Add Item'}
        </Button>
      </div>

      {items.length > 0 ? (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border p-4">
              {/* Item Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{t(`items.serviceNames.${item.name}`, item.name)}</span>
                    <span className="rounded bg-muted px-2 py-0.5 text-xs">
                      {t(`items.serviceNames.${item.serviceType}`, serviceTypeLabels[item.serviceType] || item.serviceType)}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    {item.partner && (
                      <span>{item.partner.companyName}</span>
                    )}
                    {item.serviceDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(item.serviceDate), 'MMM d, yyyy')}
                        {item.endDate && ` - ${format(new Date(item.endDate), 'MMM d, yyyy')}`}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-medium">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: item.currency,
                      }).format(item.sellingPrice * item.quantity)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {item.quantity} × {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: item.currency,
                      }).format(item.sellingPrice)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-red-500"
                    onClick={() => handleDeleteItem(item.id, item.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Payment Status */}
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {item.partnerId && item.partner && (
                  <div className="flex items-center gap-1.5">
                    <Link
                      to={`/travel/cari/accounts/partner/${item.partnerId}`}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      {item.partner.companyName}
                    </Link>
                    <span className="text-xs text-muted-foreground">—</span>
                    <Badge variant={
                      item.supplierPaymentStatus === 'PAID' ? 'default' :
                      item.supplierPaymentStatus === 'PARTIALLY_PAID' ? 'secondary' : 'destructive'
                    } className="text-xs">
                      {item.currency} {(item.supplierPaidAmount || 0).toFixed(2)}
                      /{item.totalCost.toFixed(2)}
                    </Badge>
                    {item.supplierPaymentStatus !== 'PAID' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-xs"
                        onClick={() => {
                          setSelectedItemForSupplierPayment({
                            id: item.id,
                            name: item.name,
                            totalCost: item.totalCost,
                            supplierPaidAmount: item.supplierPaidAmount || 0,
                            currency: item.currency,
                            partner: item.partner,
                          });
                          setSupplierPaymentModalOpen(true);
                        }}
                      >
                        {t('items.paySupplier') || 'Pay Supplier'}
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">{t('items.customerStatus') || 'Customer'}:</span>
                  <Badge variant={
                    (item.customerPaidAmount || 0) >= item.sellingPrice * item.quantity ? 'default' :
                    (item.customerPaidAmount || 0) > 0 ? 'secondary' : 'outline'
                  } className="text-xs">
                    {item.currency} {(item.customerPaidAmount || 0).toFixed(2)}
                    /{(item.sellingPrice * item.quantity).toFixed(2)}
                  </Badge>
                </div>
              </div>

              {/* Allocations Section */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1">
                    <Layers className="h-4 w-4" />
                    {t('allocations.title') || 'Allocations'}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleConsumeAllocation(item)}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t('allocations.add') || 'Add'}
                  </Button>
                </div>

                {item.allocationConsumptions.length > 0 ? (
                  <div className="space-y-2">
                    {item.allocationConsumptions.map((consumption) => {
                      const Icon = typeIcons[consumption.allocation.allocationType];
                      return (
                        <div
                          key={consumption.id}
                          className="flex items-center justify-between rounded-md bg-muted/50 p-2 group"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`flex h-7 w-7 items-center justify-center rounded ${typeColors[consumption.allocation.allocationType]}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{consumption.allocation.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {consumption.units} {tCommon('unit') || 'unit'}
                                {consumption.units > 1 ? 's' : ''} •{' '}
                                {consumption.allocation.partner.companyName}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: consumption.currency,
                              }).format(consumption.totalCost)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleReleaseAllocation(
                                consumption.id,
                                consumption.allocation.name
                              )}
                            >
                              <X className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    {t('allocations.noAllocations') || 'No allocations assigned'}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          {t('items.empty') || 'No booking items added yet.'}
        </p>
      )}

      {/* Add Item Modal */}
      <BookingItemModal
        open={isAddItemModalOpen}
        onOpenChange={setIsAddItemModalOpen}
        bookingId={bookingId}
        defaultServiceDate={defaultServiceDate}
        defaultEndDate={defaultEndDate}
        onSuccess={onSuccess}
      />

      {/* Consume Allocation Modal */}
      {selectedItem && (
        <AllocationConsumptionModal
          open={isConsumptionModalOpen}
          onOpenChange={setIsConsumptionModalOpen}
          bookingItemId={selectedItem.id}
          bookingItemName={selectedItem.name}
          serviceDate={selectedItem.serviceDate ? new Date(selectedItem.serviceDate) : undefined}
          endDate={selectedItem.endDate ? new Date(selectedItem.endDate) : undefined}
          onSuccess={onSuccess}
        />
      )}

      {/* Release Confirmation Dialog */}
      <AlertDialog open={!!releaseConfirm} onOpenChange={(open) => !open && setReleaseConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('allocations.releaseConfirm.title') || 'Release Allocation?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('allocations.releaseConfirm.description', { name: releaseConfirm?.allocationName }) ||
                `Are you sure you want to release "${releaseConfirm?.allocationName}"? This will return the units back to the available pool.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRelease}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('allocations.release') || 'Release'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Item Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('items.deleteConfirm.title') || 'Delete Item?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('items.deleteConfirm.description', { name: deleteConfirm?.itemName }) ||
                `Are you sure you want to delete "${deleteConfirm?.itemName}"? Any associated allocations will be released.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {tCommon('delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Supplier Payment Modal */}
      <SupplierPaymentModal
        open={supplierPaymentModalOpen}
        onOpenChange={setSupplierPaymentModalOpen}
        item={selectedItemForSupplierPayment}
        onSuccess={onSuccess}
      />
    </div>
  );
}
